import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import type { ToolExecutionContext } from '../types';
import { Tool, type ToolConstructorArgs } from './Tool';

import { FiltersEnum } from 'js/src/client';
import { ApiService } from 'js/src/client/services.gen';
import { selectMediaOptions } from 'js/src/data/media-recommendation-preferences';
import Flags from 'js/src/libs/flags';
import type { DAMConfig } from 'js/src/typings/global';

export const damMediaSearchSchema = z.object({
    queries: z
        .array(z.string())
        .describe(
            'Short, tag-like keywords to search for — NOT descriptive phrases. ' +
                'DAM search matches against tags/titles/descriptions and usually has ' +
                'no fuzzy matching, so detailed queries rarely return results. ' +
                'Instead of "campfire burning at night", pass ' +
                '["campfire", "camping", "camping night", "bonfire"]. Provide several ' +
                'variations (synonyms, broader/narrower terms, singular/plural) so ' +
                'the exact-match search has more chances to hit.',
        ),
    media_type: z
        .enum(['images', 'videos', 'all'])
        .default('all')
        .describe(
            'Type of media to search for. Default to "all" unless the user ' +
                'specifically wants images or videos.',
        ),
});
export type DamMediaSearchArgs = z.infer<typeof damMediaSearchSchema>;

function getConnectedDamConfigs(): DAMConfig[] {
    if (!window.SERVER?.USER?.dam_configs) {
        return [];
    }
    return window.SERVER.USER.dam_configs.filter(
        (config) => !config.require_connection || config.is_connected,
    );
}

function filtersForMediaType(mediaType: 'images' | 'videos' | 'all'): FiltersEnum[] {
    if (mediaType === 'images') {
        return [FiltersEnum.PHOTOS];
    }
    if (mediaType === 'videos') {
        return [FiltersEnum.VIDEOS];
    }
    return [FiltersEnum.PHOTOS, FiltersEnum.VIDEOS];
}

export class DamMediaSearchTool extends Tool<typeof damMediaSearchSchema> {
    protected override readonly schema = damMediaSearchSchema;
    protected override readonly toolName = TOOL_NAMES.searchDamMedia;
    protected override readonly toolDescription =
        "Search the user's connected DAM (Digital Asset Management) brand library for " +
        'images and videos. Prefer these brand-owned assets over stock media. Runs ' +
        'several keyword searches at once and returns the combined, de-duplicated results.';

    public override canRunAsync = true;

    public constructor(args: ToolConstructorArgs) {
        super(args);
        // Only expose this tool to the LLM when the user has a usable DAM
        // connected — same gate as the editor's DAM tab. dam_configs is fixed
        // for the page load, so evaluating once at construction is sufficient.
        this.isLLMCallable = Flags.canSeeDamTab();
    }

    public get statusIcon(): string {
        return 'folder_shared';
    }

    public override getToolDisplayName(): string {
        return 'Searching your brand library';
    }

    public async execute(args: DamMediaSearchArgs, _context: ToolExecutionContext) {
        const { queries, media_type = 'all' } = args;

        if (!selectMediaOptions(this._getState()).dam_media_enabled) {
            return `Brand (DAM) media is disabled by the user's media preferences. Use stock media with ${TOOL_NAMES.searchMedia} instead.`;
        }

        const configs = getConnectedDamConfigs();
        if (configs.length === 0) {
            return 'No connected DAM library is available to search.';
        }

        const filters = filtersForMediaType(media_type);
        const mediaRegistry = MediaRegistry.getInstance();
        const ids = new Set<string>();

        const searches = queries.flatMap((query) =>
            configs.map(async (config) => {
                try {
                    const response = await ApiService.damSearchCreate({
                        body: {
                            dam_config_id: config.id,
                            provider: config.provider,
                            filters,
                            search_term: query,
                        },
                    });
                    return response.data?.results ?? [];
                } catch (error) {
                    // A single provider or query failing shouldn't sink the whole
                    // batch — skip it and keep the results we did get. Log it so a
                    // provider outage or auth error is debuggable rather than silent.
                    console.warn(
                        `DAM search failed (config ${config.id}, ${config.provider}, query "${query}")`,
                        error,
                    );
                    return null;
                }
            }),
        );

        const settled = await Promise.all(searches);

        const attempts = settled.length;
        let failures = 0;
        for (const results of settled) {
            if (results === null) {
                failures++;
                continue;
            }
            for (const result of results) {
                try {
                    ids.add(mediaRegistry.addItem(result));
                } catch (error) {
                    console.warn('DAM search returned an unusable asset', error);
                }
            }
        }

        if (ids.size === 0) {
            // Every search erroring is a failure, not an empty library — tell the
            // agent so it can relay that rather than "your library has nothing".
            if (failures === attempts) {
                return (
                    'The brand library search failed (a provider or connection error). ' +
                    `You can retry, or fall back to stock media with ${TOOL_NAMES.searchMedia}.`
                );
            }
            return (
                `No media found in the brand library for: ${queries.join(', ')}. ` +
                `Try broader or different keywords, or fall back to stock media with ${TOOL_NAMES.searchMedia}.`
            );
        }

        const representations = Array.from(ids).map((id) => mediaRegistry.getRepresentation(id));
        return JSON.stringify(representations);
    }
}
